import { useState, useEffect, useCallback, useRef } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Input, Select, Textarea, StatusBadge, EmptyState, SearchBar,
  Pagination, ExpiryBadge, FilterBar, FilterSelect, PageHeader, ListSkeleton,
  IconButton, AlertBanner, StickyActions, Divider, cx,
  ListPage, ListSurface, stickyHeadCls, actionColCls, actionHeadCls,
  listTableCls, listCardsCls, thCls, tdCls, actionW,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, Truck, MapPin, ShieldCheck, Leaf } from "lucide-react";

const LIMIT = 15;
const INIT = {
  name: "", registrationNumber: "", type: "car", make: "", model: "", year: "",
  fuelType: "diesel", insuranceExpiry: "", pollutionExpiry: "", notes: "", status: "available",
};

const TYPES = ["car", "truck", "van", "jeep", "motorcycle", "other"];
const FUELS = ["petrol", "diesel", "electric", "cng", "other"];
const STATUSES = ["available", "assigned", "maintenance", "inactive"];

const typeIcon = { car: "🚗", truck: "🚛", van: "🚐", jeep: "🚙", motorcycle: "🏍️", other: "🚌" };
const typeTone = {
  car: "bg-brand-50 border-brand-200", truck: "bg-ink-100 border-ink-200",
  van: "bg-violet-50 border-violet-200", jeep: "bg-emerald-50 border-emerald-200",
  motorcycle: "bg-orange-50 border-orange-200", other: "bg-ink-100 border-ink-200",
};

const spec = (v) => [v.fuelType, v.make, v.model, v.year && `(${v.year})`].filter(Boolean).join(" · ");

function VehicleCard({ v, onEdit, onDelete }) {
  return (
    <Card className="p-4" hover>
      {/* actions lead the card, mirroring the table's first column */}
      <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-ink-100">
        <Button size="xs" variant="outline" icon={<Pencil size={13} />} onClick={() => onEdit(v)}>Edit</Button>
        <Button size="xs" variant="dangerSoft" icon={<Trash2 size={13} />} onClick={() => onDelete(v._id)}>Delete</Button>
      </div>

      <div className="flex items-start gap-3">
        <span className={cx("w-11 h-11 rounded-xl border grid place-items-center text-lg shrink-0", typeTone[v.type] || typeTone.other)}>
          {typeIcon[v.type] || "🚗"}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-ink-900 truncate">{v.name}</div>
          <div className="text-xs text-ink-400 capitalize truncate mt-0.5">{spec(v)}</div>
        </div>
        <StatusBadge status={v.status} />
      </div>

      <div className="flex items-center gap-2 flex-wrap mt-3">
        {v.registrationNumber && (
          <span className="font-mono text-[11px] bg-ink-100 border border-ink-200 px-2 py-1 rounded-md text-ink-700">{v.registrationNumber}</span>
        )}
        {v.assignedSite?.name && (
          <span className="inline-flex items-center gap-1 text-xs text-ink-500">
            <MapPin size={12} className="text-ink-400" />{v.assignedSite.name}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-ink-100">
        <ExpiryBadge date={v.insuranceExpiry} label="Insurance" />
        <ExpiryBadge date={v.pollutionExpiry} label="PUC" />
      </div>
    </Card>
  );
}

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INIT);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT });
      if (search) p.set("search", search);
      if (filterStatus) p.set("status", filterStatus);
      if (filterType) p.set("type", filterType);
      const { data } = await api.get(`/vehicles?${p}`);
      setVehicles(data.data.vehicles || []);
      setTotal(data.data.total);
      setPages(data.data.pages);
    } catch { toast.error("Failed to load vehicles"); }
    finally { setLoading(false); }
  }, [page, search, filterStatus, filterType]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const handleSearch = (v) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 350);
    setSearch(v);
  };

  const openCreate = () => { setForm(INIT); setSelected(null); setModal("create"); };
  const openEdit = (v) => {
    setSelected(v);
    setForm({
      name: v.name, registrationNumber: v.registrationNumber || "", type: v.type || "car",
      make: v.make || "", model: v.model || "", year: v.year || "", fuelType: v.fuelType || "diesel",
      insuranceExpiry: v.insuranceExpiry ? v.insuranceExpiry.split("T")[0] : "",
      pollutionExpiry: v.pollutionExpiry ? v.pollutionExpiry.split("T")[0] : "",
      notes: v.notes || "", status: v.status || "available",
    });
    setModal("edit");
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === "create") { await api.post("/vehicles", form); toast.success("Vehicle added!"); }
      else { await api.put(`/vehicles/${selected._id}`, form); toast.success("Vehicle updated!"); }
      setModal(null); fetchVehicles();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try { await api.delete(`/vehicles/${id}`); toast.success("Vehicle deleted"); fetchVehicles(); }
    catch { toast.error("Failed"); }
  };

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  // Insurance / PUC running out inside 30 days
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const soonExpiry = vehicles.filter(v => {
    const ins = v.insuranceExpiry ? (new Date(v.insuranceExpiry) - today) / 86400000 : 999;
    const pol = v.pollutionExpiry ? (new Date(v.pollutionExpiry) - today) / 86400000 : 999;
    return ins <= 30 || pol <= 30;
  });

  const anyFilter = search || filterStatus || filterType;

  return (
    <AdminLayout fullHeight>
      <ListPage>
        <PageHeader
          title="Vehicles"
          subtitle={`${total} vehicle${total !== 1 ? "s" : ""} in the fleet`}
          action={<Button onClick={openCreate} icon={<Plus size={16} />} className="w-full sm:w-auto">Add Vehicle</Button>}
        />

        {soonExpiry.length > 0 && (
          <div className="shrink-0">
            <AlertBanner
              type="warning"
              title={`${soonExpiry.length} vehicle${soonExpiry.length > 1 ? "s" : ""} with expiring documents`}
              message={`${soonExpiry.map(v => v.name).join(", ")} — insurance or PUC expires within 30 days.`}
            />
          </div>
        )}

        <FilterBar className="shrink-0">
          <SearchBar value={search} onChange={handleSearch} placeholder="Search name, reg. no., make…" className="flex-1 min-w-[170px]" />
          <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} ariaLabel="Filter by status" className="min-w-[130px]">
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </FilterSelect>
          <FilterSelect value={filterType} onChange={v => { setFilterType(v); setPage(1); }} ariaLabel="Filter by type" className="min-w-[120px]">
            <option value="">All Types</option>
            {TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
          </FilterSelect>
          {anyFilter && (
            <button onClick={() => { setSearch(""); setFilterStatus(""); setFilterType(""); setPage(1); }}
              className="text-xs font-semibold text-brand-600 hover:text-brand-700 px-1 whitespace-nowrap">Clear</button>
          )}
        </FilterBar>

        {loading ? (
          <div className="flex-1 min-h-0 overflow-hidden"><ListSkeleton rows={6} cols={7} /></div>
        ) : vehicles.length === 0 ? (
          <Card className="flex-1 min-h-0 grid place-items-center">
            <EmptyState icon={<Truck size={22} />} title="No vehicles found"
              description={anyFilter ? "Try adjusting your search or filters." : "Add your first vehicle to the fleet."}
              action={anyFilter
                ? <Button variant="secondary" onClick={() => { setSearch(""); setFilterStatus(""); setFilterType(""); setPage(1); }}>Clear filters</Button>
                : <Button onClick={openCreate} icon={<Plus size={16} />}>Add Vehicle</Button>} />
          </Card>
        ) : (
          <ListSurface footer={<Pagination page={page} pages={pages} total={total} limit={LIMIT} onPage={setPage} />}>
            {/* ── Desktop table ── */}
            <table className={listTableCls}>
              <thead className={stickyHeadCls}>
                <tr>
                  <th className={cx(thCls, actionHeadCls, actionW(2))}>Actions</th>
                  <th className={thCls}>Vehicle</th>
                  <th className={cx(thCls, "w-[130px]")}>Reg. No.</th>
                  <th className={thCls}>Site</th>
                  {/* insurance and PUC share a column so the row still fits the page */}
                  <th className={cx(thCls, "w-[180px]")}>Documents</th>
                  <th className={cx(thCls, "w-[120px]")}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {vehicles.map(v => (
                  <tr key={v._id} className="group hover:bg-ink-50 transition-colors">
                    <td className={cx(tdCls, actionColCls, "px-3")}>
                      <div className="flex gap-0.5">
                        <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(v)} title="Edit" />
                        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(v._id)} title="Delete" />
                      </div>
                    </td>
                    <td className={tdCls}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={cx("w-9 h-9 rounded-xl border grid place-items-center text-base shrink-0", typeTone[v.type] || typeTone.other)}>
                          {typeIcon[v.type] || "🚗"}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-ink-800 truncate">{v.name}</div>
                          <div className="text-ink-400 text-xs capitalize truncate">{spec(v)}</div>
                        </div>
                      </div>
                    </td>
                    <td className={tdCls}>
                      {v.registrationNumber
                        ? <span className="block font-mono text-[11px] bg-ink-100 border border-ink-200 px-2 py-1 rounded-md text-ink-700 truncate">{v.registrationNumber}</span>
                        : <span className="text-ink-300">—</span>}
                    </td>
                    <td className={cx(tdCls, "text-xs text-ink-500")}>
                      <div className="truncate">{v.assignedSite?.name || <span className="text-ink-300">—</span>}</div>
                    </td>
                    <td className={tdCls}>
                      <div className="flex flex-col gap-1.5">
                        <ExpiryBadge date={v.insuranceExpiry} label="Insurance" />
                        <ExpiryBadge date={v.pollutionExpiry} label="PUC" />
                      </div>
                    </td>
                    <td className={tdCls}><StatusBadge status={v.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Mobile / tablet cards ── */}
            <div className={listCardsCls}>
              {vehicles.map(v => <VehicleCard key={v._id} v={v} onEdit={openEdit} onDelete={handleDelete} />)}
            </div>
          </ListSurface>
        )}
      </ListPage>

      <Modal
        open={modal === "create" || modal === "edit"}
        onClose={() => setModal(null)}
        title={modal === "create" ? "Add Vehicle" : "Edit Vehicle"}
        subtitle={modal === "edit" ? selected?.name : "New fleet vehicle"}
        icon={<Truck size={18} />}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Vehicle Name" required {...f("name")} placeholder="e.g. Field Jeep 1" />
            <Input label="Registration No." {...f("registrationNumber")} placeholder="KL 01 AB 1234" className="font-mono uppercase" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select label="Type" {...f("type")}>
              {TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
            </Select>
            <Select label="Fuel Type" {...f("fuelType")}>
              {FUELS.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Input label="Make" {...f("make")} placeholder="Toyota" />
            <Input label="Model" {...f("model")} placeholder="Fortuner" />
            <Input label="Year" type="number" {...f("year")} placeholder="2022" wrapClass="col-span-2 sm:col-span-1" />
          </div>

          <Divider label="Document expiry" className="pt-1" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Input label="Insurance Expiry" type="date" {...f("insuranceExpiry")} icon={<ShieldCheck size={15} />} />
              {form.insuranceExpiry && <ExpiryBadge date={form.insuranceExpiry} />}
            </div>
            <div className="space-y-2">
              <Input label="Pollution (PUC) Expiry" type="date" {...f("pollutionExpiry")} icon={<Leaf size={15} />} />
              {form.pollutionExpiry && <ExpiryBadge date={form.pollutionExpiry} />}
            </div>
          </div>

          {modal === "edit" && (
            <Select label="Status" {...f("status")}>
              {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
            </Select>
          )}
          <Textarea label="Notes" {...f("notes")} placeholder="Servicing history, quirks, anything worth noting…" />

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" loading={saving} block className="sm:w-auto">
              {modal === "create" ? "Add Vehicle" : "Save Changes"}
            </Button>
          </StickyActions>
        </form>
      </Modal>
    </AdminLayout>
  );
}
